//# sourceMappingURL=effect.mjs.map
/// <reference types="./effect.d.mts" />
import * as $promise from "../../../gleam_javascript/gleam/javascript/promise.mjs";
import * as $list from "../../../gleam_stdlib/gleam/list.mjs";
import * as $unsafe from "../../../redraw/redraw/internal/unsafe.mjs";
import { toList, CustomType as $CustomType } from "../../gleam.mjs";

class Effect extends $CustomType {
  constructor(one) {
    super();
    this.one = one;
  }
}

class Effects extends $CustomType {
  constructor(all) {
    super();
    this.all = all;
  }
}

class None extends $CustomType {}

/**
 * Creates your own `Effect(state, msg)` with the corresponding side effect.
 * An `Effect(state, msg)` is created by receiving the `dispatch` & `get_state`
 * functions, used respectively to notify the store with a new message & get
 * the latest state. You can run arbitrary code in your `Effect`, and you can
 * notify the store as much time as needed everytime a new data is available.
 *
 * Your `Effect(state, msg)` can return anything, the resulting data
 * will be discarded.
 *
 * ```gleam
 * pub fn my_reusable_effect() {
 *   use dispatch, get_state <- effect.from()
 *   // Get the current state.
 *   let state = get_state()
 *   let assert Ok(request) = request.to("https://example.com")
 *   let authorization = "Bearer " <> state.access_token
 *   use content <- promise.await({
 *     request
 *     |> request.set_header("authorization", authorization)
 *     |> fetch.send
 *     |> promise.try_await(fetch.read_body)
 *   })
 *   // Get the latest, refreshed state.
 *   let state = get_state()
 *   dispatch(MyMsg(content))
 * }
 * ```
 */
export function from(effect) {
  let effect$1 = $unsafe.coerce(effect);
  return new Effect(effect$1);
}

/**
 * Creates your own `Effect(state, msg)` with the corresponding side effect.
 * An `Effect(state, msg)` is created by receiving the `dispatch` & `get_state`
 * functions, used respectively to notify the store with a new message & get
 * the latest state. You can run arbitrary code in your `Effect`, and you can
 * notify the store as much time as needed everytime a new data is available.
 *
 * Your `Effect(state, msg)` can return anything, the resulting data
 * will be discarded.
 *
 * ```gleam
 * pub fn my_reusable_effect() {
 *   use dispatch, get_state <- effect.from()
 *   // Get the current state.
 *   let state = get_state()
 *   let assert Ok(request) = request.to("https://example.com")
 *   let authorization = "Bearer " <> state.access_token
 *   use content <- promise.await({
 *     request
 *     |> request.set_header("authorization", authorization)
 *     |> fetch.send
 *     |> promise.try_await(fetch.read_body)
 *   })
 *   // Get the latest, refreshed state.
 *   let state = get_state()
 *   dispatch(MyMsg(content))
 * }
 * ```
 */
export function from_(effect) {
  let effect$1 = $unsafe.coerce(effect);
  return new Effect(effect$1);
}

/**
 * Group `Effect(state, msg)` into one `Effect(state, msg)`.
 * This allow to return multiple side effect from a single update cycle.
 *
 * ```gleam
 * fn update(state, msg) {
 *   case msg {
 *     Update ->
 *       #(state, effect.batch([effect.none(), effect.none()]))
 *   }
 * }
 * ```
 */
export function batch(effects) {
  return new Effects(
    $list.flat_map(
      effects,
      (effect) => {
        if (effect instanceof Effect) {
          let e = effect.one;
          return toList([new Effect(e)]);
        } else if (effect instanceof Effects) {
          let e = effect.all;
          return e;
        } else {
          return toList([]);
        }
      },
    ),
  );
}

/**
 * Performing a side effect after each update is not mandatory, however
 * every `update` function should return a tuple `#(State, Effect(State, Msg))`.
 * Use `effect.none()` to indicate to the runtime to not do anything.
 *
 * ```gleam
 * fn update(state, msg) {
 *   case msg {
 *     Update ->
 *       #(state, effect.none())
 *   }
 * }
 * ```
 */
export function none() {
  return new None();
}

export function map(effect, mapper) {
  if (effect instanceof Effect) {
    let one = effect.one;
    return new Effect(
      (dispatch, get_state) => {
        return one((msg) => { return dispatch(mapper(msg)); }, get_state);
      },
    );
  } else if (effect instanceof Effects) {
    let all = effect.all;
    return new Effects(
      $list.map(all, (_capture) => { return map(_capture, mapper); }),
    );
  } else {
    return effect;
  }
}
